import React from 'react';
import { connect } from 'react-redux';

import { FORMATTER_STYLE, getFormatter } from '../../utils/formatter';
import { Alert, Button, Col, Grid, Row, ToggleButton, ToggleButtonGroup } from 'react-bootstrap';
import SummaryCard from '../summarycard/SummaryCard';
import {
  FrontShadingDevice,
  SideShadingDevice,
  TopShadingDevice,
} from './ShadingDevices';
import EditorCanvas from './EditorCanvas';
import SCControlPanel from './SCControlPanel';
import SCControlPanelTable from './SCControlPanelTable';

import { computeSC } from '../../redux/section/section_component_actions';
import { stringifyErrorMessage } from '../../utils/utility';
import { defaultRejectHandler } from '../../utils/utility';

import './sc_editor.css';

export class SCEditor extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      sc: props.SC,
      isShowErrorMessage: false,
      viewMode: 'card',
      leftDevices: this.props.shadingDevices.left,
      rightDevices: this.props.shadingDevices.right,
      topDevices: this.props.shadingDevices.top,
      frontDevices: this.props.shadingDevices.front,
      windowWidth: this.props.window.width,
      windowHeight: this.props.window.height,
    };
  }

  getData() {
    return {
      area: this.state.windowWidth * this.state.windowHeight,
      SC: this.state.sc.total,
      rawSC: this.state.sc,
      structure: {
        window: {
          width: this.state.windowWidth,
          height: this.state.windowHeight,
        },
        shadingDevices: {
          left: this.state.leftDevices,
          right: this.state.rightDevices,
          top: this.state.topDevices,
          front: this.state.frontDevices,
        },
      },
    };
  }

  onComponentChanged(data) {
    this.setState(data);
  }

  componentWillReceiveProps(nextProps) {
    if (!nextProps.isCalculatingSC && nextProps.isCalculatingSC !== this.props.isCalculatingSC && !nextProps.errorMessage) {
      this.setState({ sc: nextProps.newSC });
    }

    if (nextProps.errorMessage && this.props.errorMessage !== nextProps.errorMessage) {
      this.setState({ isShowErrorMessage: true });
    }
  }

  renderSCInfo() {
    const { t } = this.props;
    const {
      sc: { total, diffuse, beam },
    } = this.state;

    const summaryItems = [
      {
        title: t('total'),
        number: total,
        unit: '',
        iconClass: 'fas fa-sun fa-3x',
      },
      {
        title: t('beam'),
        number: beam,
        unit: '',
        iconClass: 'fas fa-long-arrow-alt-left fa-3x',
      },
      {
        title: t('diffuse'),
        number: diffuse,
        unit: '',
        iconClass: 'fa fa-certificate fa-3x',
      },
    ].map(item => ({
      ...item,
      number: item.number ? getFormatter(FORMATTER_STYLE.floatFractionDigit6)(item.number) : t('n/a')
    }));

    return (
      <Grid>
        <Row>
          {summaryItems.map((item, index) => (
            <Col xs={12} sm={6} md={3} key={index}>
              <SummaryCard {...item} />
            </Col>
          ))}
        </Row>
      </Grid>
    );
  }

  renderWindow() {
    const { windowWidth, windowHeight } = this.state;
    return (
      <div
        className="window"
        style={{ width: `${windowWidth}em`, height: `${windowHeight}em` }}
      />
    );
  }

  renderShadingDevices() {
    const { leftDevices, rightDevices, frontDevices, topDevices } = this.state;

    return (
      <React.Fragment>
        {leftDevices && leftDevices.map(props => <SideShadingDevice side="left" {...props} key={props.name}/>)}
        {rightDevices && rightDevices.map(props => <SideShadingDevice side="right" {...props} key={props.name}/>)}
        {frontDevices && frontDevices.map(props => <FrontShadingDevice {...props} key={props.name}/>)}
        {topDevices && topDevices.map(props => <TopShadingDevice {...props} key={props.name}/>)}
      </React.Fragment>
    );
  }

  renderErrorMessage(errorMessage) {
    if (!errorMessage) return null;
    return (
      <Alert
        bsStyle="danger"
        style={{ 'white-space': 'pre' }}
        onDismiss={() => this.setState({ isShowErrorMessage: false })}
      >
        {errorMessage}
      </Alert>
    );
  }

  render() {
    const { isCalculatingSC, errorMessage, t } = this.props;
    const { isShowErrorMessage, windowWidth, windowHeight, viewMode } = this.state;
    const window = { width: this.state.windowWidth, height: this.state.windowHeight };
    const devices = {
      left: this.state.leftDevices,
      right: this.state.rightDevices,
      top: this.state.topDevices,
      front: this.state.frontDevices,
    };
    return (
      <div className="sc-visualized-display">
        {this.renderSCInfo()}
        {isShowErrorMessage && errorMessage && this.renderErrorMessage(stringifyErrorMessage(errorMessage))}
        <EditorCanvas
          width={windowWidth}
          height={windowHeight}
          showIndicator={true}
          showPresets={true}
        >
          {this.renderWindow()}
          {this.renderShadingDevices()}
        </EditorCanvas>
        <Button
          style={{ width: 110, alignSelf: 'flex-end' }}
          disabled={isCalculatingSC}
          onClick={() => this.props.computeSC(this.state)}
        >
          {isCalculatingSC ? `${t('calculating')}...` : t('calculate')}
        </Button>
        <ToggleButtonGroup
          name="viewMode"
          type="radio"
          value={viewMode}
          onChange={value => this.setState({ viewMode: value })}
          style={{ bottom: 10 }}
        >
          <ToggleButton name="viewMode" value="card">
            <i className="fa fa-grip-horizontal" />
          </ToggleButton>
          <ToggleButton name="viewMode" value="table">
            <i className="fa fa-table" />
          </ToggleButton>
        </ToggleButtonGroup>
        {viewMode === 'card' ? (
          <SCControlPanel
            window={window}
            shadingDevices={devices}
            onDataChanged={data => this.onComponentChanged(data)}
          />
        ) : (
          <SCControlPanelTable
            window={window}
            shadingDevices={devices}
            onDataChanged={data => this.onComponentChanged(data)}
          />
        )}
      </div>
    );
  }
}

SCEditor.defaultProps = {
  azimuth: 0,
  window: null,
  shadingDevices: null,
  SC: null,
};

const mapStateToProps = state => ({
  isCalculatingSC: state.section_component.isCalculatingSC,
  newSC: state.section_component.SC,
  errorMessage: state.section_component.SCErrorMessage,
});

const mapDispatchToProps = (dispatch, { sectionComponentId, azimuth }) => ({
  computeSC: ({ windowWidth, windowHeight, leftDevices, rightDevices, topDevices, frontDevices }) => defaultRejectHandler(
      dispatch(
        computeSC(sectionComponentId, azimuth, { width: windowWidth, height: windowHeight }, leftDevices, rightDevices, topDevices, frontDevices)
      ),
    ),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
  null,
  { withRef: true },
)(SCEditor);



// WEBPACK FOOTER //
// ./src/components/sc_editor/SCEditor.js